(() => {
  const PAIRS = {
    black: ['grey', 'white', 'cream', 'brown', 'beige'],
    grey: ['black', 'white', 'cream', 'navy'],
    white: ['black', 'grey', 'navy', 'brown'],
    brown: ['cream', 'beige', 'black', 'white'],
    cream: ['brown', 'black', 'olive', 'grey'],
    beige: ['brown', 'cream', 'black', 'olive'],
    navy: ['grey', 'white', 'cream', 'black'],
    blue: ['grey', 'white', 'black', 'navy'],
    olive: ['cream', 'black', 'beige', 'grey'],
    green: ['cream', 'black', 'grey', 'white'],
    pink: ['grey', 'white', 'cream', 'black'],
    burgundy: ['grey', 'black', 'cream', 'white'],
    red: ['black', 'grey', 'white'],
    purple: ['grey', 'white', 'black'],
    yellow: ['black', 'grey', 'white']
  };

  function colors(value) {
    const found = [];
    const parts = String(value || '').toLowerCase().split(/[\/|,&+]/);
    for (const part of parts) {
      let color = '';
      if (/black/.test(part)) color = 'black';
      else if (/charcoal|gr[ae]y|slate/.test(part)) color = 'grey';
      else if (/navy/.test(part)) color = 'navy';
      else if (/blue/.test(part)) color = 'blue';
      else if (/olive|khaki|sage/.test(part)) color = 'olive';
      else if (/cream|ivory|off\s*-?\s*white/.test(part)) color = 'cream';
      else if (/beige|sand|tan/.test(part)) color = 'beige';
      else if (/brown|mocha|chocolate/.test(part)) color = 'brown';
      else if (/burgundy|maroon|wine/.test(part)) color = 'burgundy';
      else if (/pink|blush/.test(part)) color = 'pink';
      else if (/white/.test(part)) color = 'white';
      else if (/red/.test(part)) color = 'red';
      else if (/purple|violet|lilac/.test(part)) color = 'purple';
      else if (/green|mint/.test(part)) color = 'green';
      else if (/yellow|gold/.test(part)) color = 'yellow';
      if (color && !found.includes(color)) found.push(color);
    }
    return found;
  }

  function kind(type, title) {
    const name = `${type || ''} ${title || ''}`.toLowerCase();
    if (/beanie|cap|hat|socks/.test(name)) return 'accessory';
    if (/sweatpant|sweats|jogger|\bpants?\b|\bshorts?\b/.test(name)) return 'bottom';
    if (/\bfits?\b|\bsets?\b|outfit|piece set/.test(name)) return 'set';
    if (/zip[ -]?up|jacket/.test(name)) return 'outerwear';
    if (/t[ -]?shirt|long[ -]?sleeve|\bshirt\b/.test(name)) return 'tee';
    return 'top';
  }

  function scoreColor(source, candidate, contrast) {
    const first = source[0] || 'black';
    const color = candidate[0];
    if (!color) return -1000;
    if (contrast && color !== first && source.includes(color)) return 128;
    if (color === first) return contrast ? 76 : 130;
    if (source.includes(color)) return 117;
    const index = (PAIRS[first] || PAIRS.black).indexOf(color);
    if (index >= 0) return (contrast ? 122 : 105) - index * 7;
    return ['black', 'grey', 'white', 'cream'].includes(color) ? 44 : -1000;
  }

  function sourceOptions(picker) {
    if (!picker) return [];
    if (typeof picker.getAllSelectedOptions === 'function') return picker.getAllSelectedOptions();
    const options = [];
    for (const option of picker.querySelectorAll('select option:checked, fieldset input:checked')) {
      if (option.dataset.optionName) options.push({ name: option.dataset.optionName, value: option.value });
    }
    return options;
  }

  function imageFor(item, variant, shade) {
    if (variant.image) return variant.image;
    const images = item.images || [];
    let bestImage = images[0]?.src || '';
    let bestScore = -Infinity;
    for (const image of images) {
      if (!image.src) continue;
      const filename = decodeURIComponent(image.src.split('?')[0].split('/').pop() || '');
      const text = `${image.alt || ''} ${filename}`.toLowerCase();
      const imageColors = colors(text.replace(/[-_]/g, ' '));
      let score = imageColors.includes(shade) ? 100 : imageColors.length ? -25 : 0;
      if (/\bfront\b/.test(text)) score += 12;
      if (/\bback\b/.test(text)) score -= 12;
      if (/size\s*guide|size\s*chart|measurement/.test(text)) score -= 60;
      if (/\bmodel\b|on model/.test(text)) score -= 9;
      if (score > bestScore) {
        bestScore = score;
        bestImage = image.src;
      }
    }
    return bestImage;
  }

  function selection(item, source, contrast, sourceHandle, avoidShade) {
    let best;
    let bestScore = -Infinity;
    for (const variant of item.variants || []) {
      const variantColors = colors(variant.color);
      if (!variantColors.length) variantColors.push(...colors(item.title));
      if (!variantColors.length) variantColors.push(...colors(item.images?.[0]?.alt));

      // The Urgency theme temporarily sells only Black, Grey, Navy and White.
      // Never recommend a linked variant of an unavailable color.
      if (!['black', 'grey', 'navy', 'white'].includes(variantColors[0])) continue;

      const similarity = scoreColor(source.colors, variantColors, contrast);
      if (similarity < 0) continue;
      let score = similarity;
      if (source.size && variant.size && source.size.toLowerCase() === variant.size.toLowerCase()) score += 6;
      if (source.fit && variant.fit && source.fit.toLowerCase() === variant.fit.toLowerCase()) score += 3;
      if (contrast && avoidShade && variantColors[0] === avoidShade && avoidShade !== source.colors[0]) score -= 22;
      if (sourceHandle.includes('essentials') && item.handle.includes('essentials')) score += 10;
      if (sourceHandle.includes('supreme') && item.handle.includes('supreme')) score += 10;
      if (score > bestScore) {
        bestScore = score;
        best = { item, variant, shade: variantColors[0], exact: source.colors.includes(variantColors[0]), score };
      }
    }
    return best;
  }

  class DripstockCompleteFit extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      try {
        this.candidates = JSON.parse(this.querySelector('[data-fit-products]')?.textContent || '[]');
      } catch {
        // Keep the Liquid-rendered matching cards visible if JSON enhancement fails.
        this.candidates = [];
      }
      if (this.candidates.length) this.update();
      document.addEventListener('change', (event) => {
        const picker = event.target instanceof Element ? event.target.closest('variant-picker') : null;
        if (!picker || picker !== this.currentPicker()) return;
        queueMicrotask(() => { if (this.isConnected) this.update(); });
      }, { signal: this.controller.signal });
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    currentPicker() {
      const nearby = this.closest('.product-details')?.querySelector('variant-picker');
      if (nearby) return nearby;
      return Array.from(document.querySelectorAll('main variant-picker')).find((picker) =>
        picker.dataset.productId === this.dataset.productId && !picker.closest('product-card, quick-add-dialog')
      );
    }

    update() {
      const options = sourceOptions(this.currentPicker());
      const find = (key) => options.find((entry) => entry.name.toLowerCase().includes(key))?.value || '';
      const chosenColor = find('color') || find('colour') || this.dataset.sourceColor;
      const source = {
        colors: colors(chosenColor),
        size: find('size') || this.dataset.sourceSize || '',
        fit: find('fit') || find('cut') || this.dataset.sourceFit || ''
      };
      if (!source.colors.length) source.colors = colors(this.dataset.sourceTitle);
      if (!source.colors.length) source.colors = ['black'];

      const category = kind(this.dataset.sourceType, this.dataset.sourceTitle);
      const groups = {
        top: ['bottom', 'accessory'],
        outerwear: ['bottom', 'accessory'],
        bottom: ['top', 'accessory'],
        tee: ['bottom', 'outerwear'],
        set: ['accessory', 'tee'],
        accessory: ['top', 'bottom']
      }[category] || ['bottom', 'accessory'];

      const selected = [];
      const used = new Set();
      for (let slot = 0; slot < 2; slot++) {
        const preferred = this.candidates.filter((item) => {
          const itemKind = kind(item.type, item.title);
          return itemKind === groups[slot] || (groups[slot] === 'top' && itemKind === 'outerwear');
        });
        const pool = preferred.length ? preferred : this.candidates;
        const scored = pool
          .filter((item) => !used.has(item.handle))
          .map((item) => selection(item, source, slot === 1, this.dataset.sourceHandle || '', selected[0]?.shade))
          .filter(Boolean)
          .sort((a, b) => b.score - a.score);
        const winner = scored[0];
        if (winner) {
          selected.push(winner);
          used.add(winner.item.handle);
        }
      }

      // Don't clear the server-rendered cards when there are no JS matches.
      if (!selected.length) return;
      const cards = Array.from(this.querySelectorAll('[data-fit-card]'));
      for (let i = 0; i < cards.length; i++) {
        const card = cards[i];
        const winner = selected[i];
        if (!winner) {
          card.hidden = true;
          continue;
        }
        const { item, variant, shade, exact } = winner;
        const url = new URL(item.url, window.location.origin);
        url.searchParams.set('variant', String(variant.id));
        const previewId = new URLSearchParams(window.location.search).get('preview_theme_id');
        if (previewId) url.searchParams.set('preview_theme_id', previewId);
        for (const link of card.querySelectorAll('a')) link.href = url.toString();
        card.querySelector('[data-fit-title]').textContent = item.title;
        card.querySelector('[data-fit-price]').textContent = variant.price;
        card.querySelector('[data-fit-badge]').textContent = exact ? `${shade.toUpperCase()} MATCH` : `${shade.toUpperCase()} PAIRING`;
        const img = card.querySelector('[data-fit-image]');
        const image = imageFor(item, variant, shade);
        if (image) {
          img.src = image;
          img.alt = `${item.title} in ${variant.color || shade}`;
          img.hidden = false;
        } else {
          img.hidden = true;
          img.removeAttribute('src');
        }
        card.hidden = false;
      }
      this.hidden = selected.length === 0;
    }
  }

  if (!customElements.get('dripstock-complete-fit')) {
    customElements.define('dripstock-complete-fit', DripstockCompleteFit);
  }
})();